import { Input, makeStyles, tokens } from "@fluentui/react-components";
import type { FC } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Intermediate interfaces break the circular reference that TypeScript
// cannot resolve when both array and object forms are inlined.
interface JsonArray extends Array<JsonValue> {}
interface JsonObject extends Record<string, JsonValue> {}
export type JsonValue = string | number | boolean | null | JsonArray | JsonObject;

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  key: {
    minWidth: "120px",
    maxWidth: "160px",
    fontSize: "11px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
    fontFamily: "monospace",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  input: {
    flex: 1,
    fontFamily: "monospace",
    fontSize: "11.5px",
  },
  complexValue: {
    flex: 1,
    padding: "4px 8px",
    background: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "3px",
    fontFamily: "monospace",
    fontSize: "10.5px",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "pre-wrap",
    overflowY: "auto",
    maxHeight: "80px",
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface JsonFormProps {
  value: Record<string, JsonValue>;
  onChange: (value: Record<string, JsonValue>) => void;
}

/** True iff every element of ``v`` is a string/number/boolean. Lets the
 *  form render simple lists (e.g. ``sources: ["pubmed", "clinical_trials"]``)
 *  as a comma-separated text input instead of a read-only JSON dump.
 *  Exported for tests. */
export function isPrimitiveArray(v: JsonValue): v is JsonArray {
  if (!Array.isArray(v)) return false;
  return v.every((e) => typeof e === "string" || typeof e === "number" || typeof e === "boolean");
}

/** Render an array of primitives as a comma-separated string for the
 *  text-input view. Exported for tests. */
export function joinPrimitiveArray(arr: ReadonlyArray<string | number | boolean>): string {
  return arr.map((e) => String(e)).join(", ");
}

/** Parse a comma-separated input back into a JsonArray. Trims whitespace,
 *  drops empty fragments, coerces digits-only strings to numbers when the
 *  original array's element type was numeric. Exported for tests. */
export function splitPrimitiveArray(raw: string, prevSampleType: "string" | "number" | "boolean"): JsonArray {
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (prevSampleType === "number") {
    return parts.map((s) => {
      const n = Number(s);
      return Number.isNaN(n) ? s : n;
    });
  }
  if (prevSampleType === "boolean") {
    return parts.map((s) => (s === "true" ? true : s === "false" ? false : s));
  }
  return parts;
}

/**
 * Renders a simple key→input form for a flat (or shallow) JSON object.
 * - String / number / boolean primitive values → editable Input
 * - **Array of primitives** (string[] / number[] / boolean[]) →
 *   editable comma-separated Input (2026-05-06 — fixed regression where
 *   ``sources: ["pubmed", "clinical_trials"]`` rendered as a read-only
 *   JSON dump in the LiteratureSearchGate approval form).
 * - null / nested object / array of objects → read-only display (too
 *   complex to edit inline; bail to JSON-textarea view via the parent's
 *   form/JSON toggle).
 *
 * Caller is responsible for serialising the value on submit.
 */
const JsonForm: FC<JsonFormProps> = ({ value, onChange }) => {
  const styles = useStyles();

  const handleChange = (key: string, raw: string): void => {
    const existing = value[key];
    let coerced: JsonValue = raw;

    if (typeof existing === "number") {
      const n = Number(raw);
      coerced = Number.isNaN(n) ? raw : n;
    } else if (typeof existing === "boolean") {
      if (raw === "true") {
        coerced = true;
      } else if (raw === "false") {
        coerced = false;
      }
    } else if (isPrimitiveArray(existing)) {
      // Sample the first element to decide the parsed type. Empty
      // arrays default to string[].
      const sample = existing[0];
      const sampleType: "string" | "number" | "boolean" =
        typeof sample === "number" ? "number" : typeof sample === "boolean" ? "boolean" : "string";
      coerced = splitPrimitiveArray(raw, sampleType);
    }

    onChange({ ...value, [key]: coerced });
  };

  return (
    <div className={styles.root}>
      {Object.entries(value).map(([k, v]) => {
        const isPrimitive = typeof v === "string" || typeof v === "number" || typeof v === "boolean";
        const isPrimArr = isPrimitiveArray(v);

        return (
          <div key={k} className={styles.row}>
            <span className={styles.key} title={k}>
              {k}
            </span>
            {isPrimitive ? (
              <Input
                className={styles.input}
                size="small"
                value={String(v)}
                onChange={(_, data) => handleChange(k, data.value)}
                aria-label={k}
              />
            ) : isPrimArr ? (
              <Input
                className={styles.input}
                size="small"
                value={joinPrimitiveArray(v as ReadonlyArray<string | number | boolean>)}
                onChange={(_, data) => handleChange(k, data.value)}
                aria-label={k}
                placeholder="comma-separated values"
              />
            ) : (
              <div className={styles.complexValue}>{v === null ? "null" : JSON.stringify(v, null, 2)}</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default JsonForm;
